import { type ReactNode } from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Loading from '../../../components/loading/Loading';
import COLORS from '../../../resources/style/Themes';
import {
  ArrowForward,
  MenuBook,
  LocalPolice,
} from '@mui/icons-material';
import SupportLinks from '../../../enums/SupportLinks';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';

const HEADING_GRADIENT = `linear-gradient(to right, ${COLORS.SAFEGUARD_BLUE} 23%, ${COLORS.SAFEGUARD_ORANGE})`;
const SECTION_ICON_SIZE = 22;
const ICON_BASE_SX = { display: 'block', flexShrink: 0 };
const CARD_ENTRANCE_DURATION_MS = 400;
const CARD_ENTRANCE_STAGGER_MS = 90;

interface HelpDocument {
  title: string;
  description: string;
  actionLabel: string;
  link: string;
}

interface HelpSection {
  id: string;
  label: string;
  icon: ReactNode;
  documents: HelpDocument[];
}

interface DocumentCardProps extends HelpDocument {
  entranceIndex: number;
}

const DocumentCard = ({
  title,
  description,
  actionLabel,
  link,
  entranceIndex,
}: DocumentCardProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 2,
      p: 3,
      borderRadius: '16px',
      backgroundColor: COLORS.WHITE,
      boxShadow: '0px 0px 30px 0px rgba(0, 0, 0, 0.08)',
      '@keyframes helpCardEntrance': {
        from: { opacity: 0, transform: 'translateY(12px)' },
        to: { opacity: 1, transform: 'none' },
      },
      animation: `helpCardEntrance ${CARD_ENTRANCE_DURATION_MS}ms ease-out both`,
      animationDelay: `${entranceIndex * CARD_ENTRANCE_STAGGER_MS}ms`,
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    }}
  >
    <Typography
      sx={{
        fontSize: 15,
        fontWeight: 500,
        color: COLORS.SAFEGUARD_BLUE,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: 14,
        letterSpacing: '0.13px',
        color: COLORS.GREY,
      }}
    >
      {description}
    </Typography>
    <Button
      variant="outlined"
      color="secondary"
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<ArrowForward sx={ICON_BASE_SX} />}
      sx={{
        gap: 1,
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.5,
        boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.07)',
        '& .MuiButton-endIcon': {
          ml: 0,
          alignSelf: 'center',
          '& > *:nth-of-type(1)': { fontSize: SECTION_ICON_SIZE },
        },
      }}
    >
      {actionLabel}
    </Button>
  </Box>
);

export const Help = () => {
  const { t } = useTranslation();

  const helpSections: HelpSection[] = [
    {
      id: 'training',
      label: t(StringKeys.TRAINING_MATERIALS),
      icon: <MenuBook sx={{ ...ICON_BASE_SX, fontSize: SECTION_ICON_SIZE }} />,
      documents: [
        {
          title: t(StringKeys.USER_GUIDES),
          description: t(StringKeys.USER_GUIDES_DESCRIPTION),
          actionLabel: t(StringKeys.ACCESS_USER_GUIDES),
          link: SupportLinks.USER_GUIDES,
        },
      ],
    },
    {
      id: 'legal',
      label: t(StringKeys.LEGAL_AND_COMPLIANCE),
      icon: (
        <LocalPolice sx={{ ...ICON_BASE_SX, fontSize: SECTION_ICON_SIZE }} />
      ),
      documents: [
        {
          title: t(StringKeys.END_USER_LICENSE_AGREEMENT),
          description: t(StringKeys.END_USER_LICENSE_AGREEMENT_DESCRIPTION),
          actionLabel: t(StringKeys.READ_END_USER_LICENSE_AGREEMENT),
          link: SupportLinks.END_USER_LICENSE_AGREEMENT,
        },
        {
          title: t(StringKeys.PRIVACY_POLICY),
          description: t(StringKeys.PRIVACY_POLICY_DESCRIPTION),
          actionLabel: t(StringKeys.READ_PRIVACY_POLICY),
          link: SupportLinks.PRIVACY_POLICY,
        },
      ],
    },
  ];

  // One running total so later cards stagger after earlier sections.
  const sectionStartIndexes: number[] = [];
  let cardOffset = 0;
  for (const section of helpSections) {
    sectionStartIndexes.push(cardOffset);
    cardOffset += section.documents.length;
  }

  return (
    <Container sx={{ py: 1 }}>
      <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography
            sx={{
              fontSize: 28,
              background: HEADING_GRADIENT,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              fontWeight: 500,
              color: 'transparent',
            }}
          >
            {t(StringKeys.HELP_AND_SUPPORT)}
          </Typography>
        </Box>

        {helpSections.map((section, sectionIndex) => (
          <Box key={section.id} sx={{ mb: 4, '&:last-of-type': { mb: 0 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
                color: COLORS.SAFEGUARD_BLUE,
              }}
            >
              {section.icon}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1,
                  textTransform: 'uppercase',
                }}
              >
                {section.label}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {section.documents.map((document, documentIndex) => (
                <DocumentCard
                  key={document.link}
                  title={document.title}
                  description={document.description}
                  actionLabel={document.actionLabel}
                  link={document.link}
                  entranceIndex={
                    sectionStartIndexes[sectionIndex] + documentIndex
                  }
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Container>
  );
};

const AuthenticatedHelp = withAuthenticationRequired(Help, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});

export default AuthenticatedHelp;
